/**
 * Routines to convert from generated types to friendlier ones.
 */

import {Artifact, ArtifactAlias, User} from '../../../generated/graphql';
import {getDefaultColor} from '../utils';
import {JobRuns, JobVersion, RunCreator} from './utilTypes';

export const convertArtifactToVersion = (artifact: Artifact): JobVersion => {
  const {id, createdBy, aliases} = artifact;
  const description = artifact.description ?? '';
  const createdAt = artifact.createdAt.toString();

  let createdByUser = null;
  if (createdBy.__typename === 'User') {
    createdByUser = {
      username: createdBy.username ?? '',
      photoUrl: createdBy.photoUrl ?? '',
    };
  }
  // TODO: Need an example of createdBy having __typename === 'Run'
  return {id, description, createdAt, createdByUser, aliases};
};

const convertUserToRunCreator = (user: User): RunCreator => {
  const {id, name, username, photoUrl} = user;
  return {
    id,
    name,
    username: username ?? '',
    photoUrl: photoUrl ?? '',
  };
};

// TODO: Duplicated a few places, unify.
const RE_VERSION = /^v(\d+)$/;

// Look through the aliases of a job version for the version.
// Return as a number.
export const findVersionNumber = (aliases: ArtifactAlias[]): number => {
  for (const alias of aliases) {
    const match = alias.alias.match(RE_VERSION);
    if (match) {
      return parseInt(match[1], 10);
    }
  }
  throw new Error('No version number in artifact');
};

export const extractRunState = (state: string, stopped: boolean): string => {
  if (state === 'running' && stopped) {
    return 'stopping';
  }
  return state;
};

// Aggregate the runs for every version of a Job.
export const extractRuns = (artifact: Artifact): JobRuns => {
  const runs: JobRuns = [];
  const runEdges = artifact.usedBy.edges;
  const version = findVersionNumber(artifact.aliases);
  for (const runEdge of runEdges) {
    const {node} = runEdge;
    const {
      id,
      name,
      displayName,
      state,
      stopped,
      user,
      config: configStr,
      project,
      runInfo,
    } = node;
    const createdAt = node.createdAt.toString();

    const creator = user ? convertUserToRunCreator(user) : null;

    // Note: This is the default color; user can manually override colors
    //       in the view and that is not accounted for here.
    const defaultColor = getDefaultColor(node.defaultColorIndex, name);

    const config = JSON.parse(configStr);

    // Where the Run was created, necessary for URL construction.
    const projectName = project?.name ?? '';
    const entityName = project?.entity?.name ?? '';

    runs.push({
      id,
      name,
      displayName: displayName ?? name,
      createdAt,
      state: extractRunState(state ?? 'unknown', stopped ?? false),
      creator,
      defaultColor,
      version,
      jobAliases: artifact.aliases,
      config,
      projectName,
      entityName,
      args: runInfo?.args ?? [],
    });
  }
  return runs;
};
